import Breadcrumb from "@/components/Breadcrumb"
import { useTranslation } from "react-i18next"
import { useGetAPIKeyQuery, useCreateAPIKeyMutation, useRegenerateAPIKeyMutation, useDeleteAPIKeyMutation } from "@/services/external-api.service"
import { useState } from "react"
import { formatDateTimeAutoToString } from "@/utils/format"
import { DATE_FORMAT } from "@/constants/default"
import { usePermissions } from "@/rbac"
import { permissions } from "@/rbac/permissions"

export default function ExternalAPIComponent() {
    const { t } = useTranslation()
    const { hasPermission } = usePermissions()
    const canAdd = hasPermission(permissions.EXTERNALAPI.ADD)
    const canEdit = hasPermission(permissions.EXTERNALAPI.EDIT)
    const canDelete = hasPermission(permissions.EXTERNALAPI.DELETE)
    const { data: apiKey, isLoading, refetch } = useGetAPIKeyQuery(undefined, { refetchOnMountOrArgChange: true })
    const [createAPIKey] = useCreateAPIKeyMutation()
    const [regenerateAPIKey] = useRegenerateAPIKeyMutation()
    const [deleteAPIKey] = useDeleteAPIKeyMutation()

    const [showPlainKey, setShowPlainKey] = useState(false)
    const [plainKey, setPlainKey] = useState("")
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showRegenerateModal, setShowRegenerateModal] = useState(false)
    const [newKeyInput, setNewKeyInput] = useState("")

    const handleCreateKey = async () => {
        if (!newKeyInput || newKeyInput.length !== 32) {
            alert("API key must be exactly 32 alphanumeric characters")
            return
        }
        try {
            const result = await createAPIKey({
                api_key: newKeyInput,
                name: "Discord Bot"
            }).unwrap()
            setPlainKey(result.api_key)
            setShowPlainKey(true)
            setShowCreateModal(false)
            setNewKeyInput("")
            refetch()
        } catch (error) {
            console.error("Failed to create API key:", error)
        }
    }

    const handleRegenerateKey = async () => {
        if (!newKeyInput || newKeyInput.length !== 32) {
            alert("API key must be exactly 32 alphanumeric characters")
            return
        }
        try {
            const result = await regenerateAPIKey({ new_api_key: newKeyInput }).unwrap()
            setPlainKey(result.api_key)
            setShowPlainKey(true)
            setShowRegenerateModal(false)
            setNewKeyInput("")
            refetch()
        } catch (error) {
            console.error("Failed to regenerate API key:", error)
        }
    }

    const handleDeleteKey = async () => {
        try {
            await deleteAPIKey().unwrap()
            setShowDeleteConfirm(false)
            refetch()
        } catch (error) {
            console.error("Failed to delete API key:", error)
        }
    }

    if (isLoading) {
        return (
            <>
                <Breadcrumb pageName="External API Settings" />
                <div className="flex items-center justify-center h-96">
                    <div className="text-gray-500">Loading...</div>
                </div>
            </>
        )
    }

    return (
        <>
            <Breadcrumb pageName="External API Settings" />
            <div className="flex flex-col gap-10">
                <div className="rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-8">
                    {/* Header Section */}
                    <div className="mb-6">
                        <h4 className="text-xl font-semibold text-black dark:text-white">
                            Personal Access Token (PAT)
                        </h4>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            This token allows external applications (like Discord bots) to access the SckyRoom API on behalf of authorized users.
                        </p>
                    </div>

                    {/* Current Key Display or Create Button */}
                    {apiKey ? (
                        <div className="mb-6">
                            <div className="rounded-sm border border-stroke bg-gray-2 dark:bg-meta-4 p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h5 className="font-medium text-black dark:text-white mb-1">
                                            Active API Key
                                        </h5>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Name: {apiKey.name}
                                        </p>
                                    </div>
                                    <span className="inline-flex rounded-full bg-success px-3 py-1 text-sm font-medium text-white">
                                        Active
                                    </span>
                                </div>

                                <div className="mb-3">
                                    <label className="mb-1 block text-sm font-medium text-black dark:text-white">
                                        API Key
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={apiKey.masked_key}
                                            readOnly
                                            className="w-full rounded border-[1.5px] border-stroke bg-white py-2 px-3 font-mono text-sm outline-none transition disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Created</p>
                                        <p className="font-medium text-black dark:text-white">
                                            {formatDateTimeAutoToString(apiKey.created_at, DATE_FORMAT)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Last Used</p>
                                        <p className="font-medium text-black dark:text-white">
                                            {apiKey.last_used_at ? formatDateTimeAutoToString(apiKey.last_used_at, DATE_FORMAT) : 'Never'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    {canEdit && <button
                                        onClick={() => setShowRegenerateModal(true)}
                                        className="inline-flex items-center justify-center rounded-md bg-warning py-2 px-4 text-center font-medium text-white hover:bg-opacity-90"
                                    >
                                        <i className="fa-light fa-arrow-rotate-right mr-2"></i>
                                        Replace Key
                                    </button>}
                                    {canDelete && <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="inline-flex items-center justify-center rounded-md border border-meta-1 py-2 px-4 text-center font-medium text-meta-1 hover:bg-meta-1 hover:text-white"
                                    >
                                        <i className="fa-light fa-ban mr-2"></i>
                                        Revoke Key
                                    </button>}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-6">
                            <div className="rounded-sm border border-stroke border-dashed bg-gray-2 dark:bg-meta-4 p-8 text-center">
                                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-3 dark:bg-meta-4">
                                    <i className="fa-light fa-key text-4xl text-gray-500"></i>
                                </div>
                                <h5 className="mb-2 text-lg font-medium text-black dark:text-white">
                                    No API Key Configured
                                </h5>
                                <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
                                    Add a Personal Access Token to enable external API access
                                </p>
                                {canAdd && <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="inline-flex items-center justify-center rounded-md bg-primary py-3 px-6 text-center font-medium text-white hover:bg-opacity-90"
                                >
                                    <i className="fa-light fa-plus mr-2"></i>
                                    Add API Key
                                </button>}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Plain Key Display Modal */}
            {showPlainKey && (
                <div className="fixed top-0 left-0 z-999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
                    <div className="w-full max-w-142.5 rounded-lg bg-white py-12 px-8 dark:bg-boxdark md:py-15 md:px-17.5">
                        <div className="text-center">
                            <span className="mx-auto inline-flex h-20 w-20 items-center justify-center rounded-full bg-success/10">
                                <i className="fa-light fa-circle-check text-4xl text-success"></i>
                            </span>
                            <h4 className="mt-5 text-xl font-bold text-black dark:text-white sm:text-2xl">
                                API Key Configured Successfully
                            </h4>
                            <p className="mt-3 mb-8 text-sm text-gray-600 dark:text-gray-400">
                                Your Personal Access Token has been saved and is now active for external API access.
                            </p>
                            <button
                                onClick={() => setShowPlainKey(false)}
                                className="inline-flex items-center justify-center rounded bg-primary py-3 px-6 text-center font-medium text-white hover:bg-opacity-90"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Revoke Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed top-0 left-0 z-999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
                    <div className="w-full max-w-142.5 rounded-lg bg-white py-12 px-8 text-center dark:bg-boxdark md:py-15 md:px-17.5">
                        <span className="mx-auto inline-block">
                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect opacity="0.1" width="60" height="60" rx="30" fill="#DC2626"></rect>
                                <path d="M30 27.2498V29.9998V27.2498ZM30 35.4999H30.0134H30ZM20.6914 41H39.3086C41.3778 41 42.6704 38.7078 41.6358 36.8749L32.3272 20.3747C31.2926 18.5418 28.7074 18.5418 27.6728 20.3747L18.3642 36.8749C17.3296 38.7078 18.6222 41 20.6914 41Z" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"></path>
                            </svg>
                        </span>
                        <h4 className="mt-5.5 pb-2 text-xl font-bold text-black dark:text-white sm:text-2xl">
                            Revoke API Key?
                        </h4>
                        <p className="mb-10 font-medium dark:text-white">
                            This will disable the API key immediately. All external applications using this key will stop working.
                        </p>
                        <div className="-mx-3 flex flex-row gap-y-4">
                            <div className="w-full px-3 2xsm:w-1/2">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="block w-full rounded border border-gray bg-gray p-3 text-center font-medium text-black transition hover:bg-opacity-90"
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="w-full px-3 2xsm:w-1/2">
                                <button
                                    onClick={handleDeleteKey}
                                    className="block w-full rounded border border-meta-1 bg-meta-1 p-3 text-center font-medium text-white transition hover:bg-opacity-90"
                                >
                                    Revoke Key
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create API Key Modal */}
            {showCreateModal && (
                <div className="fixed top-0 left-0 z-999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
                    <div className="w-full max-w-142.5 rounded-lg bg-white py-12 px-8 dark:bg-boxdark md:py-15 md:px-17.5">
                        <h4 className="mb-5 text-xl font-bold text-black dark:text-white sm:text-2xl">
                            Add Personal Access Token
                        </h4>

                        <div className="mb-6">
                            <div className="rounded-sm border border-primary/20 bg-primary/5 p-4 mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    <strong>How to generate a secure API key:</strong>
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1 ml-2">
                                    <li>Visit <a href="https://www.random.org/strings/?num=1&len=32&digits=on&upperalpha=on&loweralpha=on&unique=on&format=plain&rnd=new" target="_blank" rel="noopener noreferrer" className="text-primary underline font-medium">random.org String Generator</a></li>
                                    <li>Use these settings: 1 string, 32 characters, digits + upper + lowercase</li>
                                    <li>Click "Get Strings" to generate a random key</li>
                                    <li>Copy the generated key and paste it below</li>
                                </ol>
                            </div>
                            <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                API Key (exactly 32 alphanumeric characters)
                            </label>
                            <input
                                type="text"
                                value={newKeyInput}
                                onChange={(e) => setNewKeyInput(e.target.value)}
                                placeholder="Paste your 32-character alphanumeric key"
                                maxLength={32}
                                className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-4 font-mono text-sm outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
                            />
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                {newKeyInput.length}/32 characters
                            </p>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setShowCreateModal(false)
                                    setNewKeyInput("")
                                }}
                                className="rounded border border-gray bg-gray px-4 py-2 text-center font-medium text-black transition hover:bg-opacity-90"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateKey}
                                disabled={newKeyInput.length !== 32}
                                className="rounded bg-primary px-4 py-2 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Save Key
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Replace API Key Modal */}
            {showRegenerateModal && (
                <div className="fixed top-0 left-0 z-999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
                    <div className="w-full max-w-142.5 rounded-lg bg-white py-12 px-8 dark:bg-boxdark md:py-15 md:px-17.5">
                        <h4 className="mb-5 text-xl font-bold text-black dark:text-white sm:text-2xl">
                            Replace Personal Access Token
                        </h4>

                        <div className="mb-6">
                            <div className="rounded-sm border border-warning bg-warning/10 p-4 mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Warning:</strong> This will immediately revoke the old key. All applications using the old key will stop working.
                                </p>
                            </div>
                            <div className="rounded-sm border border-primary/20 bg-primary/5 p-4 mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    <strong>How to generate a secure API key:</strong>
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1 ml-2">
                                    <li>Visit <a href="https://www.random.org/strings/?num=1&len=32&digits=on&upperalpha=on&loweralpha=on&unique=on&format=plain&rnd=new" target="_blank" rel="noopener noreferrer" className="text-primary underline font-medium">random.org String Generator</a></li>
                                    <li>Use these settings: 1 string, 32 characters, digits + upper + lowercase</li>
                                    <li>Click "Get Strings" to generate a random key</li>
                                    <li>Copy the generated key and paste it below</li>
                                </ol>
                            </div>
                            <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                New API Key (exactly 32 alphanumeric characters)
                            </label>
                            <input
                                type="text"
                                value={newKeyInput}
                                onChange={(e) => setNewKeyInput(e.target.value)}
                                placeholder="Paste your new 32-character alphanumeric key"
                                maxLength={32}
                                className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-4 font-mono text-sm outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
                            />
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                {newKeyInput.length}/32 characters
                            </p>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setShowRegenerateModal(false)
                                    setNewKeyInput("")
                                }}
                                className="rounded border border-gray bg-gray px-4 py-2 text-center font-medium text-black transition hover:bg-opacity-90"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRegenerateKey}
                                disabled={newKeyInput.length !== 32}
                                className="rounded bg-warning px-4 py-2 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Replace Key
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
